/**
 * GraphPlatformTab — Visualizes and manages the Neo4j graph platform config.
 *
 * Shows agent nodes, MCP servers, tools, toolsets, and graph health as read
 * from the /api/admin/graph/* endpoints (Archimedes).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Bot, Server, Wrench, CheckCircle, XCircle,
  Loader2, ChevronRight, Database, Layers, Package,
  Users, Plus, X, ShieldCheck
} from 'lucide-react';
import type { GraphPlatformData } from './types';
import { authFetch, API_BASE } from './constants';

// Per-user grant on an AgentAccessLists row, as returned by
// GET /api/admin/agents/:agentId/access-list. Mirrors the
// shape from apps/express-api/routes/adminManagement.js.
interface AccessRow {
  userEmail: string;
  role: 'admin' | 'user';
  accessLevel: 'allowed' | 'denied';
  addedBy: string;
  addedAt: string;
  notes?: string;
}

interface AccessState {
  loading: boolean;
  rows: AccessRow[];
  error: string | null;
}

const EMPTY_ACCESS_STATE: AccessState = { loading: false, rows: [], error: null };

interface GraphPlatformTabProps {
  data: GraphPlatformData | null;
  loading: boolean;
  onRefresh: () => void;
}

const SOURCE_BADGE: Record<string, { label: string; color: string }> = {
  askaldo: { label: 'Scout Central', color: '#3b82f6' },
  archimedes: { label: 'Archimedes', color: '#14b8a6' },
};

function SourceBadge({ source }: { source?: string }) {
  const cfg = SOURCE_BADGE[source || ''] || { label: source || 'unknown', color: '#6f747b' };
  const isAskAldo = source === 'askaldo';
  return (
    <span style={{
      fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px',
      backgroundColor: isAskAldo ? 'var(--idx-bg-inset)' : 'var(--idx-bg-success)',
      color: 'var(--idx-text-muted)', border: '1px solid var(--idx-border)',
      fontWeight: 500,
    }}>
      {cfg.label}
    </span>
  );
}

// Inline panel rendered at the bottom of an expanded agent card for
// agents in PLATFORM_AGENTS_WITH_EMAIL_GRANTS. Reads/writes go to the
// existing /api/admin/agents/:agentId/access-list endpoints. The same
// AgentAccessLists rows are honored at request time by checkAgentAccess
// and at listing time by getUserAccessibleAgents (via the allowlist
// surfacing block), so granting a teammate immediately exposes the
// agent in their chat picker.
const AgentAccessPanel: React.FC<{
  agentId: string;
  agentName: string;
  state: AccessState;
  newEmail: string;
  adding: boolean;
  onNewEmailChange: (v: string) => void;
  onAdd: () => void;
  onRevoke: (email: string) => void;
  onRefresh: () => void;
}> = ({ agentId, agentName, state, newEmail, adding, onNewEmailChange, onAdd, onRevoke, onRefresh }) => {
  const grantedRows = state.rows.filter(r => r.accessLevel === 'allowed');
  return (
    <div style={{
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: '1px dashed var(--idx-border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <ShieldCheck size={14} style={{ color: 'var(--idx-text-muted)' }} />
        <strong style={{ fontSize: '0.85rem', color: 'var(--idx-text-primary)' }}>
          Access ({grantedRows.length})
        </strong>
        <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)' }}>
          Grant {agentName} to a teammate by email
        </span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={state.loading}
          title="Refresh"
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: '1px solid var(--idx-border)',
            color: 'var(--idx-text-muted)',
            borderRadius: '6px',
            padding: '2px 6px',
            fontSize: '0.7rem',
            cursor: state.loading ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <RefreshCw size={11} className={state.loading ? 'idx-spinner' : ''} />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
        <input
          type="email"
          placeholder="teammate@unifirst.com"
          value={newEmail}
          onChange={(e) => onNewEmailChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !adding) onAdd(); }}
          style={{
            flex: 1,
            padding: '6px 10px',
            fontSize: '0.8rem',
            borderRadius: '6px',
            border: '1px solid var(--idx-border)',
            background: 'var(--idx-bg-primary)',
            color: 'var(--idx-text-primary)',
          }}
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={adding || !newEmail.trim()}
          style={{
            padding: '6px 12px',
            fontSize: '0.8rem',
            borderRadius: '6px',
            border: '1px solid var(--idx-border)',
            background: 'var(--idx-bg-success)',
            color: 'var(--idx-text-primary)',
            cursor: adding || !newEmail.trim() ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontWeight: 500,
          }}
        >
          {adding ? <Loader2 size={12} className="idx-spinner" /> : <Plus size={12} />}
          Add
        </button>
      </div>

      {state.error && (
        <div style={{
          padding: '6px 10px',
          marginBottom: '8px',
          fontSize: '0.75rem',
          background: 'var(--idx-bg-error)',
          color: 'var(--idx-text-primary)',
          border: '1px solid var(--idx-border)',
          borderRadius: '6px',
        }}>
          {state.error}
        </div>
      )}

      {state.loading && grantedRows.length === 0 ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>
          <Loader2 size={12} className="idx-spinner" /> Loading access list…
        </div>
      ) : grantedRows.length === 0 ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>
          No teammates have been granted access yet. Admins (Entra-role super-admins
          and named agent admins) always have access independent of this list.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {grantedRows.map(row => (
            <li
              key={row.userEmail}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                borderRadius: '6px',
                background: 'var(--idx-bg-inset)',
                border: '1px solid var(--idx-border)',
              }}
            >
              <Users size={12} style={{ color: 'var(--idx-text-muted)' }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-primary)', flex: 1 }}>
                {row.userEmail}
              </span>
              {row.role === 'admin' && (
                <span style={{
                  fontSize: '0.65rem',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  background: 'var(--idx-bg-success)',
                  color: 'var(--idx-text-primary)',
                  fontWeight: 500,
                }}>
                  admin
                </span>
              )}
              {row.addedBy && (
                <span style={{ fontSize: '0.7rem', color: 'var(--idx-text-muted)' }}>
                  by {row.addedBy}
                </span>
              )}
              {row.role !== 'admin' && (
                <button
                  type="button"
                  onClick={() => onRevoke(row.userEmail)}
                  title={`Revoke ${row.userEmail}`}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--idx-border)',
                    color: 'var(--idx-text-muted)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <X size={10} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {/* agentId is intentionally not displayed inline here — the agent
          row above already shows it in monospace. Keeping this panel
          focused on the human-readable access list. */}
      <div style={{ display: 'none' }} aria-hidden>{agentId}</div>
    </div>
  );
};

const GraphPlatformTab: React.FC<GraphPlatformTabProps> = ({ data, loading, onRefresh }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>('agents');
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [expandedMcp, setExpandedMcp] = useState<string | null>(null);

  // ── Email-grant management for opted-in platform agents ──────────────
  // Agents in `emailGrantAllowlist` render an inline "Access" panel that
  // calls the existing /api/admin/agents/:agentId/access-list endpoints.
  // The allowlist is server-driven (PLATFORM_AGENTS_WITH_EMAIL_GRANTS in
  // agentAccessControl.js) so adding an agent to the set unlocks the UI
  // without a frontend redeploy.
  const [emailGrantAllowlist, setEmailGrantAllowlist] = useState<Set<string>>(new Set());
  const [accessByAgent, setAccessByAgent] = useState<Record<string, AccessState>>({});
  const [newEmailByAgent, setNewEmailByAgent] = useState<Record<string, string>>({});
  const [addingByAgent, setAddingByAgent] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/admin/platform-agents/email-grant-allowlist`);
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled && body && Array.isArray(body.agentIds)) {
          setEmailGrantAllowlist(new Set(body.agentIds));
        }
      } catch {
        // Non-fatal: panel just won't render for any agents. The chat-side
        // gate still works through the underlying access-list table.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadAccess = useCallback(async (agentId: string) => {
    setAccessByAgent(prev => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || EMPTY_ACCESS_STATE), loading: true, error: null },
    }));
    try {
      const res = await authFetch(
        `${API_BASE}/admin/agents/${encodeURIComponent(agentId)}/access-list?limit=200`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const body = await res.json();
      const rows: AccessRow[] = Array.isArray(body.accessList) ? body.accessList : [];
      setAccessByAgent(prev => ({
        ...prev,
        [agentId]: { loading: false, rows, error: null },
      }));
    } catch (err) {
      setAccessByAgent(prev => ({
        ...prev,
        [agentId]: {
          loading: false,
          rows: prev[agentId]?.rows || [],
          error: err instanceof Error ? err.message : 'Failed to load access list',
        },
      }));
    }
  }, []);

  // Lazy-load on first expand of an allowlisted agent. Re-expanding the
  // same agent doesn't re-fetch — refresh is via the explicit Refresh
  // button in the panel header.
  useEffect(() => {
    if (!expandedAgent) return;
    if (!emailGrantAllowlist.has(expandedAgent)) return;
    const existing = accessByAgent[expandedAgent];
    if (existing && (existing.loading || existing.rows.length > 0 || existing.error)) return;
    loadAccess(expandedAgent);
  }, [expandedAgent, emailGrantAllowlist, accessByAgent, loadAccess]);

  const handleAddGrant = useCallback(async (agentId: string) => {
    const email = (newEmailByAgent[agentId] || '').trim();
    if (!email || !email.includes('@')) {
      setAccessByAgent(prev => ({
        ...prev,
        [agentId]: {
          ...(prev[agentId] || EMPTY_ACCESS_STATE),
          error: 'Enter a valid email',
        },
      }));
      return;
    }
    setAddingByAgent(prev => ({ ...prev, [agentId]: true }));
    try {
      const res = await authFetch(
        `${API_BASE}/admin/agents/${encodeURIComponent(agentId)}/access-list`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: email, accessLevel: 'allowed', role: 'user' }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setNewEmailByAgent(prev => ({ ...prev, [agentId]: '' }));
      await loadAccess(agentId);
    } catch (err) {
      setAccessByAgent(prev => ({
        ...prev,
        [agentId]: {
          ...(prev[agentId] || EMPTY_ACCESS_STATE),
          error: err instanceof Error ? err.message : 'Failed to add grant',
        },
      }));
    } finally {
      setAddingByAgent(prev => ({ ...prev, [agentId]: false }));
    }
  }, [newEmailByAgent, loadAccess]);

  const handleRevokeGrant = useCallback(async (agentId: string, userEmail: string) => {
    if (!window.confirm(`Revoke access to ${agentId} for ${userEmail}?`)) return;
    try {
      const res = await authFetch(
        `${API_BASE}/admin/agents/${encodeURIComponent(agentId)}/access-list/${encodeURIComponent(userEmail)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      await loadAccess(agentId);
    } catch (err) {
      setAccessByAgent(prev => ({
        ...prev,
        [agentId]: {
          ...(prev[agentId] || EMPTY_ACCESS_STATE),
          error: err instanceof Error ? err.message : 'Failed to revoke grant',
        },
      }));
    }
  }, [loadAccess]);

  if (loading) {
    return (
      <div className="idx-loading">
        <Loader2 className="idx-spinner" size={32} />
        <p>Loading graph platform data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="idx-empty-state">
        <Database size={48} />
        <h3>Graph Platform Config</h3>
        <p>Load the Neo4j graph to view agents, MCP servers, tools, and relationships managed in Archimedes.</p>
        <button className="idx-refresh-btn" onClick={onRefresh}>
          <RefreshCw size={16} />
          Load Graph
        </button>
      </div>
    );
  }

  const { agents, mcpServers, tools, toolSets, schema, health } = data;

  const toggleSection = (s: string) => setExpandedSection(expandedSection === s ? null : s);

  return (
    <div className="idx-registry-tab">
      {/* Health Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', borderRadius: '10px', marginBottom: '16px',
        background: health.connected ? 'var(--idx-bg-success)' : 'var(--idx-bg-error)',
        border: '1px solid var(--idx-border)',
      }}>
        {health.connected
          ? <CheckCircle size={18} style={{ color: 'var(--idx-text-primary)' }} />
          : <XCircle size={18} style={{ color: 'var(--idx-text-primary)' }} />}
        <div>
          <strong style={{ fontSize: '0.9rem', color: 'var(--idx-text-primary)' }}>
            Neo4j Graph: {health.connected ? 'Connected' : 'Disconnected'}
          </strong>
          {health.error && (
            <div style={{ fontSize: '0.8rem', color: 'var(--idx-text-primary)', marginTop: '2px' }}>{health.error}</div>
          )}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button className="idx-refresh-btn" onClick={onRefresh} style={{ fontSize: '0.8rem' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="idx-registry-stats">
        <div className="idx-stat-card" style={{ borderLeft: '3px solid #3b82f6' }}>
          <Bot size={16} style={{ color: 'var(--idx-text-link, #3b82f6)' }} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{agents.length}</span>
            <span className="idx-stat-label">Agents</span>
          </div>
        </div>
        <div className="idx-stat-card" style={{ borderLeft: '3px solid #14b8a6' }}>
          <Server size={16} style={{ color: '#14b8a6' }} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{mcpServers.length}</span>
            <span className="idx-stat-label">MCP Servers</span>
          </div>
        </div>
        <div className="idx-stat-card" style={{ borderLeft: '3px solid #f59e0b' }}>
          <Wrench size={16} style={{ color: '#f59e0b' }} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{tools.length}</span>
            <span className="idx-stat-label">Tools</span>
          </div>
        </div>
        <div className="idx-stat-card" style={{ borderLeft: '3px solid #8b5cf6' }}>
          <Package size={16} style={{ color: '#8b5cf6' }} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{toolSets.length}</span>
            <span className="idx-stat-label">Tool Sets</span>
          </div>
        </div>
      </div>

      {/* Schema Summary */}
      {schema && schema.nodes.length > 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: '10px', marginBottom: '16px',
          background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Layers size={16} style={{ color: 'var(--idx-text-muted)' }} />
            <strong style={{ fontSize: '0.85rem', color: 'var(--idx-text-secondary)' }}>Graph Schema</strong>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {schema.nodes.map((n, i) => (
              <span key={i} style={{
                fontSize: '0.75rem', padding: '3px 10px', borderRadius: '12px',
                background: 'var(--idx-bg-inset)', color: 'var(--idx-text-link)', fontWeight: 500,
                border: '1px solid var(--idx-border)',
              }}>
                {n.labels.join(':')} ({n.count})
              </span>
            ))}
          </div>
          {schema.relationships.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {schema.relationships.map((r, i) => (
                <span key={i} style={{
                  fontSize: '0.7rem', padding: '2px 8px', borderRadius: '8px',
                  background: 'var(--idx-bg-warn)', color: 'var(--idx-text-primary)', fontFamily: 'monospace',
                  border: '1px solid var(--idx-border)',
                }}>
                  {r.from[0]} -[{r.type}]→ {r.to[0]} ({r.count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── AGENTS ─────────────────────────────────────────────── */}
      <div className="idx-registry-roadmap" style={{ marginBottom: '16px' }}>
        <h3
          onClick={() => toggleSection('agents')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <ChevronRight size={16} className={expandedSection === 'agents' ? 'rotated' : ''} style={{ transition: 'transform 0.2s' }} />
          <Bot size={18} /> Agents ({agents.length})
        </h3>

        {expandedSection === 'agents' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
            {agents.map(agent => {
              const isExp = expandedAgent === agent.agentId;
              return (
                <div key={agent.agentId} style={{
                  padding: '10px 14px', borderRadius: '8px',
                  background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)',
                }}>
                  <div
                    onClick={() => setExpandedAgent(isExp ? null : agent.agentId)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                  >
                    <Bot size={16} style={{ color: agent.disabled ? 'var(--idx-text-faint)' : '#3b82f6' }} />
                    <strong style={{ flex: 1, color: agent.disabled ? 'var(--idx-text-faint)' : 'var(--idx-text-primary)' }}>{agent.name}</strong>
                    <code style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)' }}>{agent.agentId}</code>
                    <SourceBadge source={agent.source} />
                    {agent.status && (
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px',
                        background: agent.status === 'active' ? 'var(--idx-bg-success)' : 'var(--idx-bg-warn)',
                        color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)',
                      }}>
                        {agent.status}
                      </span>
                    )}
                    {agent.disabled && (
                      <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '6px', background: 'var(--idx-bg-error)', color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)' }}>disabled</span>
                    )}
                    <ChevronRight size={14} style={{ transform: isExp ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                  </div>

                  {isExp && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--idx-border)' }}>
                      {agent.description && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--idx-text-secondary)', margin: '0 0 8px' }}>{agent.description}</p>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px', fontSize: '0.8rem' }}>
                        {agent.endpoint && <div><span style={{ color: 'var(--idx-text-muted)' }}>Endpoint:</span> <code>{agent.endpoint}</code></div>}
                        {agent.streamingEndpoint && <div><span style={{ color: 'var(--idx-text-muted)' }}>Streaming:</span> <code>{agent.streamingEndpoint}</code></div>}
                        {agent.healthEndpoint && <div><span style={{ color: 'var(--idx-text-muted)' }}>Health:</span> <code>{agent.healthEndpoint}</code></div>}
                        {agent.version && <div><span style={{ color: 'var(--idx-text-muted)' }}>Version:</span> {agent.version}</div>}
                        {agent.sharepointFolder && <div><span style={{ color: 'var(--idx-text-muted)' }}>SharePoint:</span> {agent.sharepointFolder}</div>}
                        <div><span style={{ color: 'var(--idx-text-muted)' }}>Streaming:</span> {agent.isStreaming ? 'Yes' : 'No'}</div>
                        <div><span style={{ color: 'var(--idx-text-muted)' }}>A2A:</span> {agent.supportsA2A ? 'Yes' : 'No'}</div>
                        <div><span style={{ color: 'var(--idx-text-muted)' }}>Featured:</span> {agent.featured ? 'Yes' : 'No'}</div>
                      </div>
                      {/* Relationships */}
                      {agent.mcpServers && agent.mcpServers.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>MCP Servers: </span>
                          {agent.mcpServers.map(s => (
                            <span key={s} style={{
                              fontSize: '0.75rem', padding: '2px 8px', borderRadius: '8px',
                              background: 'var(--idx-bg-success)', color: 'var(--idx-text-primary)',
                              marginRight: '4px', border: '1px solid var(--idx-border)',
                            }}>{s}</span>
                          ))}
                        </div>
                      )}
                      {agent.toolSets && agent.toolSets.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>Tool Sets: </span>
                          {agent.toolSets.map(t => (
                            <span key={t} style={{
                              fontSize: '0.75rem', padding: '2px 8px', borderRadius: '8px',
                              background: 'var(--idx-bg-inset)', color: 'var(--idx-text-link)',
                              marginRight: '4px', border: '1px solid var(--idx-border)',
                            }}>{t}</span>
                          ))}
                        </div>
                      )}
                      {agent.owners && agent.owners.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>Owners: </span>
                          {agent.owners.map((o, i) => (
                            <span key={i} style={{ fontSize: '0.8rem', marginRight: '8px' }}>
                              {o.name} ({o.email})
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Access panel — only renders for platform agents
                          whose agentId is in the server-side email-grant
                          allowlist (PLATFORM_AGENTS_WITH_EMAIL_GRANTS). */}
                      {emailGrantAllowlist.has(agent.agentId) && (
                        <AgentAccessPanel
                          agentId={agent.agentId}
                          agentName={agent.name}
                          state={accessByAgent[agent.agentId] || EMPTY_ACCESS_STATE}
                          newEmail={newEmailByAgent[agent.agentId] || ''}
                          adding={!!addingByAgent[agent.agentId]}
                          onNewEmailChange={(v) =>
                            setNewEmailByAgent(prev => ({ ...prev, [agent.agentId]: v }))
                          }
                          onAdd={() => handleAddGrant(agent.agentId)}
                          onRevoke={(email) => handleRevokeGrant(agent.agentId, email)}
                          onRefresh={() => loadAccess(agent.agentId)}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── MCP SERVERS ────────────────────────────────────────── */}
      <div className="idx-registry-roadmap" style={{ marginBottom: '16px' }}>
        <h3
          onClick={() => toggleSection('mcp')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <ChevronRight size={16} className={expandedSection === 'mcp' ? 'rotated' : ''} style={{ transition: 'transform 0.2s' }} />
          <Server size={18} /> MCP Servers ({mcpServers.length})
        </h3>

        {expandedSection === 'mcp' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
            {mcpServers.length === 0 && (
              <p style={{ fontSize: '0.85rem', color: 'var(--idx-text-muted)' }}>No MCP servers registered in the graph.</p>
            )}
            {mcpServers.map(mcp => {
              const isExp = expandedMcp === mcp.name;
              return (
                <div key={mcp.name} style={{
                  padding: '10px 14px', borderRadius: '8px',
                  background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)',
                }}>
                  <div
                    onClick={() => setExpandedMcp(isExp ? null : mcp.name)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                  >
                    <Server size={16} style={{ color: '#14b8a6' }} />
                    <strong style={{ flex: 1 }}>{mcp.name}</strong>
                    {mcp.protocol && <code style={{ fontSize: '0.7rem', color: 'var(--idx-text-muted)' }}>{mcp.protocol}</code>}
                    <SourceBadge source={mcp.source} />
                    {mcp.status && (
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px',
                        background: mcp.status === 'active' ? 'var(--idx-bg-success)' : 'var(--idx-bg-error)',
                        color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)',
                      }}>
                        {mcp.status}
                      </span>
                    )}
                    <ChevronRight size={14} style={{ transform: isExp ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                  </div>

                  {isExp && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--idx-border)', fontSize: '0.85rem' }}>
                      {mcp.description && <p style={{ color: 'var(--idx-text-secondary)', margin: '0 0 8px' }}>{mcp.description}</p>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div><span style={{ color: 'var(--idx-text-muted)' }}>URL:</span> <code>{mcp.url}</code></div>
                        {mcp.environment && <div><span style={{ color: 'var(--idx-text-muted)' }}>Environment:</span> {mcp.environment}</div>}
                      </div>
                      {mcp.tools && mcp.tools.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <span style={{ color: 'var(--idx-text-muted)' }}>Tools: </span>
                          {mcp.tools.map(t => (
                            <span key={t} style={{
                              fontSize: '0.75rem', padding: '2px 8px', borderRadius: '8px',
                              background: 'var(--idx-bg-warn)', color: 'var(--idx-text-primary)',
                              marginRight: '4px', border: '1px solid var(--idx-border)',
                            }}>{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── TOOLS ──────────────────────────────────────────────── */}
      {tools.length > 0 && (
        <div className="idx-registry-roadmap" style={{ marginBottom: '16px' }}>
          <h3
            onClick={() => toggleSection('tools')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <ChevronRight size={16} className={expandedSection === 'tools' ? 'rotated' : ''} style={{ transition: 'transform 0.2s' }} />
            <Wrench size={18} /> Tools ({tools.length})
          </h3>

          {expandedSection === 'tools' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
              {tools.map(tool => (
                <div key={tool.name} style={{
                  padding: '8px 14px', borderRadius: '8px',
                  background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <Wrench size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <strong style={{ fontSize: '0.85rem' }}>{tool.name}</strong>
                  {tool.toolType && <code style={{ fontSize: '0.7rem', color: 'var(--idx-text-muted)' }}>{tool.toolType}</code>}
                  {tool.description && <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', flex: 1 }}>{tool.description}</span>}
                  <SourceBadge source={tool.source} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TOOL SETS ──────────────────────────────────────────── */}
      {toolSets.length > 0 && (
        <div className="idx-registry-roadmap" style={{ marginBottom: '16px' }}>
          <h3
            onClick={() => toggleSection('toolsets')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <ChevronRight size={16} className={expandedSection === 'toolsets' ? 'rotated' : ''} style={{ transition: 'transform 0.2s' }} />
            <Package size={18} /> Tool Sets ({toolSets.length})
          </h3>

          {expandedSection === 'toolsets' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
              {toolSets.map(ts => (
                <div key={ts.name} style={{
                  padding: '10px 14px', borderRadius: '8px',
                  background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Package size={14} style={{ color: '#8b5cf6' }} />
                    <strong style={{ fontSize: '0.85rem' }}>{ts.name}</strong>
                    {ts.description && <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>{ts.description}</span>}
                    <SourceBadge source={ts.source} />
                  </div>
                  {ts.tools && ts.tools.length > 0 && (
                    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {ts.tools.map(t => (
                        <span key={t.name} style={{
                          fontSize: '0.72rem', padding: '2px 8px', borderRadius: '8px',
                          background: 'var(--idx-bg-warn)', color: 'var(--idx-text-primary)',
                          border: '1px solid var(--idx-border)',
                        }}>
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { GraphPlatformTab };
